import { faArrowRight, faCheck, faPlus, faQuestionCircle, faTrashAlt } from '@fortawesome/pro-solid-svg-icons'
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useUploadMutation } from '../../../../Redux/Api/Api';
import {
    useAddLogoMutation, useImportCardsMutation, usePullFeedMutation,
    usePutNewsFeedMutation,
    usePutSocialsMutation,
    usePutThemeMutation,
} from '../../../../Redux/Api/Organisations';
import Card from '../../../Shared/Card/Card'
import Button from '../../../Shared/Form/Button'
import Title from '../../../Shared/Title'
import WizardLine from './WizardLine'
import { JWT } from '../../../../Utils/JWT'
import { OrganisationSettings, Social } from '../../../../Redux/Api/Request/Organisations'
import ColorPicker from '../../../Shared/Form/ColorPicker'
import ThemePreview from '../../../Shared/ThemePreview'
import FilePicker from '../../../Shared/Form/FilePicker'
import { AnimatePresence, motion } from 'framer-motion'
import Input from '../../../Shared/Form/Input'
import Dropdown from '../../../Shared/Form/Dropdown'
import { all, max, min } from '../../../../Utils/InValidation'
import Form from '../../../Shared/Form/Form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ModalContext } from '../../../Shared/Modal/Modal'
import BusyArea from '../../../Shared/BusyArea'
import StatusPopups from '../../../Shared/Form/StatusPopups'
import { Hydra } from '../../../../Utils/Hydra'
import { useAppDispatch, useAppSelector } from '../../../../Redux/store'
import { putSettingsLocal, putThemeLocal } from '../../../../Redux/State/Organisation'
import Constants from '../../../../Constants'
import Switch from "../../../Shared/Form/Switch";
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import GenericDatagrid from '../../../Datagrid/GenericDatagrid';

type DeepPartial<T> = T extends object ? {
    [P in keyof T]?: DeepPartial<T[P]>;
} : T;

type FormattedCsvRow = {
    id: number;
    initials: string;
    lastname: string;
    email: string;
    membershipId: string;
    activeFrom: string;
    activeUntil: string;
    type: string;
    dateOfBirth: string;
    location: string;
    phone: string;
    department: string;
    numberOfPeople: string;
    countryCode: string;
    discount: string;
    team: string;
};

type CsvColumn = { field: string; headerName: string; width: number };

const WizardPage = () => {

    const organisation_name = JWT.read<string>('organisation_name')
    const org_id = useAppSelector((s) => s.organisation.id)!
    const theme = useAppSelector((s) => s.organisation.settings?.theme)

    const { t, i18n } = useTranslation()

    const steps = [
        t('wizzard:stepInformation'),
        t('wizzard:stepTheme'),
        t('wizzard:stepSocials'),
        t('wizzard:stepNews'),
        t('wizzard:stepImport')
    ];

    const modalContext = useContext(ModalContext)

    const dispatch = useAppDispatch()

    const [progress, setProgress] = useState(0)
    const [busy, setBusy] = useState(false)
    const [tint, setTint] = useState('#E6171A')
    const [lighterTint, setLighterTint] = useState('#ed4245')
    const [fileBlob, setFileBlob] = useState('')
    const [socials, setSocials] = useState<Array<Social>>([])
    const [rssInfoOpen, setRssInfoOpen] = useState(false)
    const [cardFile, setCardFile] = useState<File | null>(null)
    const [fileIsValid, setFileIsValid] = useState<string>('')
    const [csvData, setCsvData] = useState<Array<Array<string>>>()
    const [isDataOpen, setIsDataOpen] = useState(false)
    const [error, setError] = useState('')
    const [height, setHeight] = useState(0)

    const [formattedCsvData, setFormattedCsvData] = useState<FormattedCsvRow[]>([])
    const [csvColumns, setCsvColumns] = useState<CsvColumn[]>([])

    const [putTheme] = usePutThemeMutation()
    const [uploadFile] = useUploadMutation()
    const [addLogo] = useAddLogoMutation()
    const [putSocials] = usePutSocialsMutation()
    const [putFeed] = usePutNewsFeedMutation()
    const [pullFeed] = usePullFeedMutation()
    const [importCards] = useImportCardsMutation()

    const ref = useRef<HTMLDivElement>(null)

    const checkHeight = (add?: number) => {
        if (!ref.current) return

        let rect = ref.current.getBoundingClientRect()

        setHeight(rect.height + (add || 0))
    }

    useEffect(() => {
        checkHeight(progress == 2 ? 50 : 0)
        setTimeout(() => checkHeight(50), 100)
        setRssInfoOpen(false)
    }, [progress])

    useEffect(() => {
        if (!theme) return

        setTint(theme.colors.tint)
        setLighterTint(theme.colors.lighterTint)

        setFileBlob(theme.icon)
    }, [theme])

    const addSocial = () => {
        setSocials((s) => [...s, {
            text: '',
            url: '',
            type: ''
        }])

        // 🤮
        setTimeout(() => checkHeight(50), 100)
        setTimeout(() => document.getElementById(`social${socials.length}`)?.scrollIntoView(), 200)

    }

    const deleteSocial = (index: number) => {
        setSocials((s) => {
            let cp = [...s]

            cp.splice(index, 1)

            return cp
        })

        setTimeout(() => checkHeight(50), 100)
    }

    const trimCSV = (csv: string) => {
        const lines = csv.split('\n').map(line => line.trim());

        // Remove empty lines at the end
        while (lines[lines.length - 1] === '') {
            lines.pop();
        }

        return lines.join('\n');
    };

    useEffect(() => {
        if (!cardFile) return

        setFileIsValid('')

            ; (async () => {
                const fileExtension = cardFile.name.split('.').pop()?.toLowerCase();

                if (fileExtension !== 'xlsx' && fileExtension !== 'xls') {
                    setFileIsValid(t('cards:import:onlyXlsxAllowed') as string);
                    setTimeout(() => checkHeight(50), 100)
                    return;
                }

                // Import XLSXLoader dynamically
                const { XLSXLoader } = await import('../../../../Services/XLSXLoader');
                const loadedData = await XLSXLoader.load(cardFile, t);

                if (loadedData.error) {
                    setFileIsValid(loadedData.error);
                    setTimeout(() => checkHeight(50), 100)
                    return;
                }

                if (!loadedData.data || loadedData.data.length === 0) {
                    setFileIsValid('Geen data gevonden in het bestand');
                    setTimeout(() => checkHeight(50), 100)
                    return;
                }

                setCsvData(loadedData.data);
                setIsDataOpen(true);

                const columnHeaders = [
                    { field: 'id', headerName: t('cards:list:line'), width: 70, getValue: (params: any) => params.row.id + 1, renderCell: (params: any) => params.row.id + 1 },
                    { field: 'initials', headerName: t('cards:list:initials'), width: 100 },
                    { field: 'lastname', headerName: t('cards:list:lastname'), width: 150 },
                    { field: 'email', headerName: t('cards:list:email'), width: 200 },
                    { field: 'membershipId', headerName: t('cards:list:membershipId'), width: 150 },
                    { field: 'activeFrom', headerName: t('cards:list:activeFrom'), width: 150 },
                    { field: 'activeUntil', headerName: t('cards:list:activeUntil'), width: 150 },
                    { field: 'type', headerName: t('cards:list:type'), width: 100 },
                    { field: 'dateOfBirth', headerName: t('cards:list:dateOfBirth'), width: 150 },
                    { field: 'location', headerName: t('cards:list:location'), width: 150 },
                    { field: 'phone', headerName: t('cards:list:phone'), width: 150 },
                    { field: 'department', headerName: t('cards:list:department'), width: 150 },
                    { field: 'numberOfPeople', headerName: t('cards:edit:numberOfPeople'), width: 150 },
                    { field: 'countryCode', headerName: t('cards:list:countryCode'), width: 150 },
                    { field: 'discount', headerName: t('cards:list:discount'), width: 100 },
                    { field: 'team', headerName: t('cards:list:team'), width: 100 }
                ];

                setCsvColumns(columnHeaders);

                const formattedData = loadedData.data.map((row, index) => {
                    // Note: row[0] is the line number added by XLSXLoader
                    const rowData = {
                        id: index,
                        initials: row[1] || '',
                        lastname: row[2] || '',
                        email: row[3] || '',
                        membershipId: row[4] || '',
                        activeFrom: row[5] || '',
                        activeUntil: row[6] || '',
                        type: row[7] || '',
                        dateOfBirth: row[8] || '',
                        location: row[9] || '',
                        phone: row[10] || '',
                        department: row[11] || '',
                        numberOfPeople: row[12] || '',
                        countryCode: row[13] || '',
                        discount: row[14] || '',
                        team: row[15] || ''
                    };
                    return rowData;
                });

                setFormattedCsvData(formattedData);

                setTimeout(() => checkHeight(50), 100)
            })()

    }, [cardFile, t])

    useEffect(() => checkHeight(50), [fileIsValid])
    useEffect(() => void setTimeout(() => checkHeight(50), 100), [isDataOpen])

    useEffect(() => {
        dispatch(putThemeLocal({
            background: '',
            normal: '',
            border: '',
            inactive: '',
            text: '',
            lighterText: '',
            tint: tint,
            lighterTint: lighterTint
        }))
    }, [tint, lighterTint])

    const finish = async (data: {
        feed: string,
        cards?: File,
        send: boolean,
        tint: string,
        lighterTint: string,
        logo?: File
    }) => {
        if (busy) return

        if (fileIsValid && !(await modalContext.show(
            t("wizzard:error1"),
            t("wizzard:error2") + fileIsValid
        ))) {
            modalContext.hide();
            return;
        }


        modalContext.hide()

        setBusy(true)

        let formattedData: Array<Social> = []

        for (let [k, v] of Object.entries(data)) {
            if (!k.startsWith('social_')) continue
            let [_, index, name] = k.split('_')

            if (!formattedData[parseInt(index)]) {
                (formattedData[parseInt(index)] as any) = {}
            }

            (formattedData[parseInt(index)] as any)[name] = v
        }

        const settings: OrganisationSettings = {
            theme: {
                colors: {
                    background: '',
                    normal: '',
                    border: '',
                    inactive: '',
                    text: '',
                    lighterText: '',
                    tint: data.tint,
                    lighterTint: data.lighterTint
                },
                icon: ''
            },
            socials: formattedData,
            news: {
                feed: data.feed
            },
            organisationLanguage: 'NL',
            organisationEmail: 'suport@digipas.app',
            hidden: false,
            LastReminderEmail: null
        }

        try {
            await putTheme({
                id: org_id,
                settings: settings.theme.colors
            }).unwrap()

            if (data.logo) {
                const upload_id = await uploadFile({
                    file: data.logo
                }).unwrap()

                await addLogo({
                    organisation_id: org_id,
                    upload_id
                })
            }

            if (socials.length > 0) {
                await putSocials({
                    id: org_id,
                    socials: settings.socials
                }).unwrap()
            }

            if (settings.news.feed) {
                await putFeed({
                    id: org_id,
                    feed: settings.news.feed
                }).unwrap()

                try {
                    await pullFeed(org_id).unwrap()
                } catch (e) { }
            }

            if (data.cards) {
                const file_id = await uploadFile({
                    file: data.cards
                }).unwrap()

                const formatDateString = (dateString: string): string => {
                    return dateString.replace(/\s*\(.*?\)\d{2}:\d{2}/, '');
                };
                const selectedDateTime = formatDateString(new Date().toDateString())

                await importCards([org_id, file_id, data.send, selectedDateTime]).unwrap()
            }

            dispatch(putSettingsLocal(settings))

            window.location.pathname = `/${i18n.language}/dashboard/success`
        } catch (e) {
            let err = e as Hydra.Error
            setError(err['hydra:description'])
            setBusy(false)
        }
    }

    const finishSkip = async () => {
        if (busy) return

        setBusy(true)

        try {
            await putTheme({
                id: org_id,
                settings: theme!.colors!
            }).unwrap()

            window.location.pathname = `/${i18n.language}/dashboard`
        } catch (e) {
            let err = e as Hydra.Error
            setError(err['hydra:description'])
            setBusy(false)
        }
    }

    return (
        <Form className='flex flex-grow min-h-full items-center justify-center' nostyle submit={finish}>
            <Card rounded className='flex flex-col w-full 2xl:w-7/12 my-20'>
                <BusyArea busy={busy}>
                    <Title text={t("wizzard:headText")} subtitle={t("wizzard:headTextPrefix") || " "} />
                    <WizardLine current={progress} items={steps} />
                    <motion.div transition={{ ease: [0.25, 1, 0.5, 1] }} animate={{ height }} className='flex flex-col relative'>
                        <div ref={ref} className='absolute w-full flex flex-col my-4 gap-4 min-h-[20rem]'>
                            <div className={`${progress !== 0 && 'hidden'} flex flex-col`}>
                                <Trans i18nKey="wizzard:info" values={{ organisation_name: organisation_name }} components={{ br: <br /> }} />

                            </div>
                            <div className={`${progress !== 1 && 'hidden'} flex flex-col`}>
                                <div className='flex flex-col'>
                                    <Trans i18nKey="wizzard:infoTheme" components={{ br: <br /> }} />
                                </div>
                                <ColorPicker initial='#E6171A' color={tint} onChangeReactive={setTint} id='tint' label={t("wizzard:primaryColor")} />
                                <ColorPicker initial='#ed4245' color={lighterTint} onChangeReactive={setLighterTint} id='lighterTint' label={t("wizzard:secondaryColor")} />
                                <FilePicker id='logo' label='Logo' onChangeBlob={setFileBlob} accept={['image/*']} />
                                <ThemePreview fullWidth tint={tint} lighterTint={lighterTint} fileBlob={fileBlob} />
                            </div>
                            <div className={`${progress !== 2 && 'hidden'} flex flex-col`}>
                                <div className='flex flex-col'>
                                    {t("wizzard:infoSocials")}
                                </div>
                                <div className='flex'>
                                    <Button nosubmit action={addSocial} icon={faPlus} iconright title={t("wizzard:addSocials")} />
                                </div>
                                <div className='flex flex-col relative gap-4 mt-4'>
                                    <AnimatePresence>
                                        {socials.map((s, i) => (
                                            <motion.div
                                                key={JSON.stringify(s)}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 1.1, height: 0 }}
                                                id={`social${i}`}
                                                className='flex flex-col flex-grow w-full'>
                                                <Card rounded outline>
                                                    <Input placeholder={t("wizzard:suggestionName") || " "} id={`social_${i}_text`} initial={s.text} required invalidator={all(min(3), max(30))} label={t("wizzard:name")} sublabel={'Social ' + (i + 1)} />
                                                    <Input type='url' placeholder={t("wizzard:suggestionURL") || " "} id={`social_${i}_url`} initial={s.url} required invalidator={min(3)} label='URL' />
                                                    <Dropdown id={`social_${i}_type`} initial={s.type} options={{ facebook: 'Facebook', twitter: 'X', other: 'Overig' }} label={t("wizzard:type")} />
                                                    <div className='flex'>
                                                        <Button secondary nosubmit action={() => deleteSocial(i)} title={t("wizzard:delete")} icon={faTrashAlt} />
                                                    </div>
                                                </Card>
                                            </motion.div>
                                        ))}
                                    </AnimatePresence>
                                </div>
                            </div>
                            <div className={`${progress !== 3 && 'hidden'} flex flex-col gap-2`}>
                                <div className='flex flex-col'>
                                    {t("wizzard:infoNews")}
                                    <div>
                                        {t("wizzard:infoRSSFeed")}
                                        <FontAwesomeIcon onClick={() => setRssInfoOpen(s => !s)} className='cursor-pointer opacity-60 ml-1' icon={faQuestionCircle} />
                                    </div>
                                    <motion.div
                                        animate={{ scale: rssInfoOpen ? 1 : 0, opacity: rssInfoOpen ? 1 : 0 }}
                                        className='origin-top-left bg-light-200 p-4 mt-2'>{t("wizzard:infoRSSFeedExpanded1")} <a target='_blank' className='text-accent hover:text-accent-light' href='https://x-interactive.nl/contact/'>{t("wizzard:infoRSSFeedExpanded2")}</a>.</motion.div>
                                </div>
                                <Input type='url' id='feed' label='RSS Feed URL' />
                            </div>
                            <div className={`${progress !== 4 && 'hidden'} flex flex-col`}>
                                <StatusPopups setText={setFileIsValid} type='success' subtitle={t("cards:import:error") as string} text={fileIsValid} />
                                <StatusPopups setText={setError} type='error' text={error} />
                                <div className='flex flex-col'>
                                    {t("wizzard:infoImport")}
                                </div>
                                <a className='my-2' href={Constants.DOWNLOAD_URL_CARDS_TEMPLATE_XLSX} download><Button secondary nosubmit title={t("wizzard:downloadTemplate")} /></a>
                                <FilePicker onChange={setCardFile} accept={['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']} label={t("wizzard:CSVFile")} id='cards' />
                                
                                {csvData && (
                                    <>
                                        <Button
                                            title={isDataOpen ? t('cards:import:hideExample') : t('cards:import:watchExample')}
                                            className='mb-6'
                                            nosubmit
                                            action={() => setIsDataOpen(s => !s)} />
                                            {(isDataOpen && formattedCsvData.length > 0) && (
                                                <div className="w-full overflow-x-auto">
                                                    <GenericDatagrid
                                                        data={formattedCsvData}
                                                        columns={csvColumns}
                                                        className="w-full "
                                                    />
                                                </div>
                                            )}
                                    </>
                                )}
                                <Switch id='send' label={t('cards:list:sendMail?')} />
                            </div>
                        </div>
                    </motion.div>
                    <div className='flex justify-between'>
                        <Button disabled={progress == 0} action={() => setProgress((s) => Math.max(s - 1, 0))} secondary nosubmit title={t("wizzard:back")} icon={faArrowLeft} />
                        {progress !== steps.length - 1 && <Button action={() => setProgress((s) => Math.min(s + 1, steps.length - 1))} nosubmit title={t("wizzard:next")} icon={faArrowRight} iconright />}
                        {progress === steps.length - 1 && <Button title={t("wizzard:finnish")} icon={faCheck} iconright />}
                    </div>
                </BusyArea>
                <div onClick={modalContext.withModal({ title: t("wizzard:skipAllText1"), body: t("wizzard:skipAllText2") }, finishSkip)} className='absolute right-0 -bottom-10 px-6 cursor-pointer hover:underline'><Trans i18nKey="wizzard:skipAll" components={{ b: <b /> }} /></div>
            </Card>
        </Form>
    )
}

export default WizardPage